import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import Modal from "common/Modal.jsx";
import { handleWarnings, policiesBatchCommand } from "./api";
import { safeStr, getPoliciesInRules } from "common/api";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import { ActionsRow } from "views/Status/Policies/Common";
import View from "common/View";
import help from "help/viewMonitoringPolicies.md";
import Table from "common/graphs/Table/Table";
import { NumberCell } from "common/graphs/Table/TableRenders";

const doesNothing = () => { };

const returnView = "viewMonitoringPolicies";

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const FooterMessage = styled.div`
  color: red;
  ul {
    padding: 0 0 0 15px;
    margin: 0px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
      text-align: left;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  names = [],
  whenDone,
  rules = [],
}) => {
  const { delete: itemsToDelete, notDelete } = names.reduce(
    (acc, name) => {
      if (rules.includes(name)) {
        acc.notDelete.push(name);
      } else {
        acc.delete.push(name);
      }
      return acc;
    },
    { delete: [], notDelete: [] }
  );

  const handleApply = () => {
    const cmds = itemsToDelete.map((name) => `clear policy monitor ${safeStr(name)}`);
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then(handleWarnings)
      .then(() => {
        setShowDeleteModal(false);
        whenDone();
      })
      .catch((error) => showModalError(error, ""));
  };
  if (!showDeleteModal) {
    return null;
  }
  const title =
    itemsToDelete.length === 1
      ? `Remove "${itemsToDelete[0]}" policy?`
      : `Remove ${itemsToDelete.length} policies?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={names.length > 1 && names.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {itemsToDelete.length === 1 ? (
            <p>
              WARNING: All policy data will be lost and all subscribers assigned
              to this policy will be reassigned to a default monitoring policy
              following the BQN configured rules (normally default policy is
              "monitor-default").
            </p>
          ) : null}
          {itemsToDelete.length > 1 ? (
            <>
              {itemsToDelete.length < 100 ? (
                <ul>
                  {itemsToDelete.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              ) : null}
              <p>
                WARNING: All policies data will be lost and all subscribers
                assigned to these policies will be reassigned to a default
                monitoring policy following the BQN configured rules (normally
                default policy is "monitor-default").
              </p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      footerMessage={
        notDelete.length > 0 ? (
          <FooterMessage>
            <p>These policies cannot be deleted, because they are part of a rule.</p>
            <ul>
              {notDelete.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </FooterMessage>
        ) : null
      }
      onApply={handleApply}
    />
  );
};

function onAddPolicy() {
  globalNavigate("viewPolicyMonitorEdit", {
    returnView: "viewMonitoringPolicies",
  });
}

export const PoliciesList = () => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const [request, setRequest] = useState(null);

  const doRequest = (mode, bqnId) => {
    return ifCl
      .run(policiesBatchCommand(mode, bqnId), "batch_exmode")
      .then((response) => {
        const [policiesResponse, configResponse] = response.split(batchExModeSeparator);
        const rules = getPoliciesInRules(configResponse, "monitor");
        return { response: policiesResponse, rules };
      })
      .catch((error) => console.log(error));
  };

  const doLoad = (mode, bqnId) => {
    setRequest(doRequest(mode, bqnId));
  };

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the command would not carry its option, and the response would be that of
  // the whole cluster. The effect runs again when it does.
  useEffect(
    () => (bqnId === undefined ? undefined : doLoad(mode, bqnId)),
    [mode, bqnId]
  );

  if (request === null) {
    return null;
  }

  return (
    <View
      title="Monitoring Policies"
      mdHelp={help}
      options={[{ label: "Add Policy", onClick: onAddPolicy }]}
    >
      <div className="body table-responsive">
        <ActionsRow doCreatePolicy={onAddPolicy} />
        <Request during={request}>
          {(result) => (
            <ShowPolicies
              {...result}
              reload={() => doLoad(mode, bqnId)}
              returnView={returnView}
            />
          )}
        </Request>
      </div>
    </View>
  );
};

/*****************************************************
 * Policies Table
 * ****************************************************/

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;

const openThroughputPerPolicy = (
  policyType = "monitor",
  directions = "downSpeed",
) => {
  globalNavigate("viewThroughputPerPolicy", {
    policyType,
    directions,
    returnView,
  });
};

const getColumns = ({
  reload,
  returnView,
  setDeleteSelected,
  setShowDeleteModal,
}) => {
  return [
    {
      label: "NAME",
      id: "NAME",
      numeric: false,
      cellClassName: "hyperlink-text",
      onClick: (value) => doEditPolicy(value, returnView),
    },
    {
      label: "ACTIVE-SUBSCRIBERS",
      id: "SUBSCRIBERS",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} precision={0} hasNA={true} />
      ),
    },
    {
      label: "MBYTES-DOWNLINK",
      id: "MBYTES-DOWNLINK",
      numeric: true,
      cellClassName: "hyperlink-text",
      renderCell: (value) => (
        <NumberCell value={value} precision={3} hasNA={true} />
      ),
      onClick: () => {
        openThroughputPerPolicy("monitor", "downSpeed");
      },
    },
    {
      label: "MBYTES-UPLINK",
      id: "MBYTES-UPLINK",
      numeric: true,
      cellClassName: "hyperlink-text",
      renderCell: (value) => (
        <NumberCell value={value} precision={3} hasNA={true} />
      ),
      onClick: () => {
        openThroughputPerPolicy("monitor", "upSpeed");
      },
    },
    {
      label: "UDR-GENERATION-%",
      id: "UDR",
      numeric: true,
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Edit",
          icon: "edit",
          onClick: (value) => {
            doEditPolicy(value["NAME"], returnView);
          },
        },
        {
          label: "Delete",
          icon: "delete",
          onClick: (value) => {
            setDeleteSelected([value["NAME"]]);
            setShowDeleteModal(true);
          },
          hide: (value) => value["NAME"] === "monitor-default",
        },
      ],
    },
  ];
};

export const ShowPolicies = ({
  response,
  rules = [],
  reload = doesNothing,
  returnView,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [header, ...rows] = response.trim(/\s/).split("\n");
  const headerAttrs = header.trim(/\s/).split(/\s+/);
  const [selected, setSelected] = useState([]);
  const attrIndexMap = headerAttrs.reduce((acc, attr, index) => {
    acc[attr] = index;
    return acc;
  }, {});
  const newRows = rows.map((row, index) => {
    const rowAttrs = row.trim(/\s/).split(/\s+/);
    const newEntry = rowAttrs.reduce(
      (acc, attr, index) => {
        acc[headerAttrs[index]] = attr;
        return acc;
      },
      { id: index },
    );
    return newEntry;
  });

  return (
    <>
      <TableContainer>
        <Table
          isSelectable={true}
          isRowSelectable={false}
          data={newRows}
          sort={true}
          columns={getColumns({
            reload,
            returnView,
            setDeleteSelected,
            setShowDeleteModal,
          })}
          search={true}
          csvPrefix="monitoring-policies"
          rowsPerPageNr={10}
          selected={selected}
          setSelected={(value) => {
            setSelected(value);
          }}
          deleteSelected={(values) => {
            const selectedNames = newRows
              .filter((row) => values.includes(row.id))
              .map((row) => row["NAME"]);
            setDeleteSelected(selectedNames);
            setShowDeleteModal(true);
          }}
        />
      </TableContainer>
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        names={deleteSelected}
        whenDone={() => {
          setSelected([]);
          reload();
        }}
        rules={rules}
      />
    </>
  );
};

const doEditPolicy = (policy, returnView) => {
  globalNavigate("viewPolicyMonitorEdit", {
    returnView,
    tab: 2,
    policy,
  });
};
